import { Trans, useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from '@/components';
import { ChevronDown, ChevronUp, Info } from 'react-feather';
import { Tag } from '@/components/tag';
import { PlatformIcon } from '@/utils/platformIcon';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { Tooltip } from 'antd/lib';
import Link from 'next/link';
import IconExpired from '../../../../public/assets/icons/clock_expired.svg';
import IconDeclined from '../../../../public/assets/icons/declined-document.svg';
import styles from './CollaborationItem.module.css';

dayjs.extend(duration);

type CollaborationItemType = 'INVITE' | 'ONGOING' | 'DECLINED_OR_EXPIRED' | 'DONE';
interface CollaborationItemHeadProps {
  type: CollaborationItemType;
}
interface CollaborationItemProps {
  collab: Api.Influencer.CollaborationSearchItem;
  type: CollaborationItemType;
  link?: string;
  onCampaignClick?: () => void;
  onBeatClick?: (id: string) => void;
}

interface MyCustomElemProps {
  href?: string;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  children: React.ReactNode;
}

const BEATS_TO_SHOW = 3;

export function CollaborationItemHead({ type }: CollaborationItemHeadProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.head}>
      <div className={styles.headTask}>{t('inviteView.task')}</div>
      <div className={styles.headName}>{t('collaboration.name')}</div>
      <div className={styles.headPlatform}>{t('collaboration.platform')}</div>
      <div
        className={`${styles.headDraftDate} ${type === 'INVITE' || type === 'ONGOING' ? styles.hide : ''}`}
      >
        {t('campaignPageDrafts.headingDraft')}
      </div>
      <div className={styles.headPublicationDate}>{t('invite.publicationDate')}</div>
      {type !== 'DECLINED_OR_EXPIRED' && (
        <div className={styles.headReportDate}>
          {type !== 'INVITE' && t('collaboration.reportDate')}
        </div>
      )}
      {type !== 'DECLINED_OR_EXPIRED' && (
        <div className={styles.headStatus}>
          {type !== 'INVITE' && t('campaignPageOverview.dataStatus')}
        </div>
      )}
      {(type === 'INVITE' || type === 'ONGOING') && (
        <div className={styles.headNearest}>
          {type === 'INVITE' && t('campaignPageDrafts.headingDraft')}
          {type === 'ONGOING' && t('campaignPageOverview.dateNearestStatus')}
        </div>
      )}
    </div>
  );
}

const beatStatuses = {
  INVITE_NOT_SENT: {
    label: 'Curation',
    color: '#ccc'
  },
  INVITE_SENT: {
    label: 'Invited',
    color: '#ccc'
  },
  INVITE_DECLINED: {
    label: 'Declined',
    color: '#ccc'
  },
  INVITE_EXPIRED: {
    label: 'Expired',
    color: '#ccc'
  },
  DRAFTS_NOT_SUBMITTED: {
    label: 'Drafts',
    color: '#EE7541'
  },
  DRAFTS_SUBMITTED: {
    label: 'Submitted',
    color: '#7AD098'
  },
  DRAFTS_REJECTED: {
    label: 'Rejected',
    color: 'red'
  },
  DRAFTS_IN_REVIEW: {
    label: 'In Review',
    color: '#7AD098'
  },
  PUBLISH: {
    label: 'Publish',
    color: '#ccc'
  },
  SCREENSHOTS1: {
    label: 'Metrics',
    color: '#ccc'
  },
  SCREENSHOTS2: {
    label: 'Metrics 2',
    color: '#ccc'
  },
  DONE: {
    label: 'Done',
    color: '#474E54'
  },
  CANCELED: {
    label: 'Canceled',
    color: 'red'
  },
  UNKNOWN: {
    label: 'Unknown',
    color: '#ccc'
  },
  SCREENSHOTS: {
    label: 'Metrics',
    color: '#ccc'
  }
};

const paymentStatus = {
  DONE: 'payout.status.IN_PROCESS',
  DONE_AND_PAID: 'payout.status.PAID'
};

function ItemComponent({ href, className, onClick, children }: MyCustomElemProps) {
  if (href) {
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <div className={className} onClick={onClick}>
      {children}
    </div>
  );
}

export function CollaborationItem({
  collab,
  type,
  link,
  onCampaignClick,
  onBeatClick
}: CollaborationItemProps) {
  const [showAll, setShowAll] = useState(false);
  const { t } = useTranslation();

  const displayedBeats = showAll ? collab.beats : collab.beats.slice(0, BEATS_TO_SHOW);

  const handleShowAll = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowAll(!showAll);
  };

  function getRelevantDate(beat: Api.Beat) {
    if (
      ['DRAFTS_NOT_SUBMITTED', 'DRAFTS_SUBMITTED', 'DRAFTS_REJECTED', 'DRAFTS_IN_REVIEW'].includes(
        beat.status
      )
    ) {
      return { statusType: 'Draft', status: beat.status, date: beat.date_draft };
    }
    if (beat.status === 'PUBLISH') {
      return { statusType: 'Publication', status: beat.status, date: beat.date_publish };
    }
    if (['SCREENSHOTS1', 'SCREENSHOTS2', 'SCREENSHOTS'].includes(beat.status)) {
      return { statusType: 'Report', status: beat.status, date: beat.date_result };
    }
    return null;
  }

  const getExpirationMessage = (dateExpire: Date) => {
    const expireDate = dayjs(dateExpire);
    const now = dayjs();
    const differenceInDays = expireDate.diff(now, 'day');

    if (differenceInDays < 0) {
      return <span>expired</span>;
    }
    if (differenceInDays === 0) {
      return <span className={styles.expireToday}>{t('collaborations.today')} 🔥</span>;
    }
    return (
      <span
        className={styles.expireFewDays}
      >{` ${differenceInDays + 1} ${t('collaborations.days')}`}</span>
    );
  };

  function getDateClass(
    dateType: 'Draft' | 'Publication' | 'Report',
    status: BeatStatus,
    date: Date
  ) {
    const today = dayjs();
    const targetDate = dayjs(date);
    const isPastDate = targetDate.isBefore(today, 'day');
    const isToday = targetDate.isSame(today, 'day');

    switch (dateType) {
      case 'Draft':
        if (['PUBLISH', 'SCREENSHOTS1', 'SCREENSHOTS2', 'DONE'].includes(status)) {
          return 'today';
        }
        if (isPastDate) {
          return 'missed';
        }
        if (isToday) {
          return 'today';
        }
        return null;
      case 'Publication':
        if (['SCREENSHOTS1', 'SCREENSHOTS2', 'DONE'].includes(status)) {
          return 'today';
        }
        if (isPastDate) {
          return 'missed';
        }
        if (isToday) {
          return 'today';
        }

        return null;
      case 'Report':
        if (['DONE'].includes(status)) {
          return 'today';
        }
        if (isPastDate) {
          return 'missed';
        }
        if (isToday) {
          return 'today';
        }
        return null;
      default:
        return null;
    }
  }

  return (
    <ItemComponent
      className={`${styles.collab} ${type === 'INVITE' ? styles.invite : ''}`}
      href={link}
      onClick={onCampaignClick}
    >
      <div className={styles.collabHead}>
        <div className={styles.collabName}>
          {collab?.brand_logo_url && <img src={collab.brand_logo_url} alt="" />}
          <div>
            <div className={styles.headBrand}>{collab.brand_name}</div>
            <div className={styles.headCampaignName}>{collab.campaign_name}</div>
          </div>
        </div>
        <div className={styles.collabInfo}>
          {type === 'DECLINED_OR_EXPIRED' && (
            <div className={styles.infoStatus}>
              {collab.status === 'INVITE_EXPIRED' && (
                <>
                  <IconExpired />
                  <span className={styles.expired}>{t('collaborations.expired')}</span>
                </>
              )}
              {collab.status === 'INVITE_DECLINED' && (
                <>
                  <IconDeclined />
                  <span>{t('campaignTab.declined')}</span>
                </>
              )}
            </div>
          )}
          <div className={styles.infoTotal}>
            {!collab.amount && !collab.currency ? (
              type !== 'INVITE' ? (
                '——'
              ) : (
                ''
              )
            ) : (
              <>
                <span>{collab.amount}</span> {collab.currency}
              </>
            )}
          </div>
          {type === 'INVITE' && (
            <>
              <div className={styles.infoDeadline}>
                {t('collaborations.expiredIn')}:{getExpirationMessage(collab.date_expire)}
                <div style={{ padding: '2px' }} onClick={(e) => e.stopPropagation()}>
                  <Tooltip
                    title={
                      <Trans
                        i18nKey="collaborations.expireMsg"
                        values={{
                          days: dayjs(collab.date_expire).diff(dayjs(), 'day')
                            ? dayjs(collab.date_expire).diff(dayjs(), 'day') + 1
                            : dayjs(collab.date_expire).diff(dayjs(), 'day')
                        }}
                        components={{
                          1: <span />
                        }}
                      />
                    }
                  >
                    <Info size={14} color="#474E5450" />
                  </Tooltip>
                </div>
              </div>
              <div className={styles.infoBriefButton}>
                <Button theme="cta">{t('inviteView.firstCollabModal.button')}</Button>
              </div>
            </>
          )}
          {type === 'DONE' && (
            <div className={`${styles.paidStatus} ${styles[collab.status]}`}>
              {t(paymentStatus[collab.status as 'DONE' | 'DONE_AND_PAID'])}
            </div>
          )}
        </div>
      </div>
      <div className={styles.collabHeadSm}>
        <div className={styles.headRow}>
          <div className={`${styles.rowTitle} ${collab?.brand_logo_url ? styles.spacing : ''}`}>
            {collab?.brand_logo_url && <img src={collab.brand_logo_url} alt="" />}
            {collab.brand_name}
          </div>
          <div className={`${styles.rowInfo} ${styles.rowInfoTotal}`}>
            {!collab.amount && !collab.currency ? (
              type !== 'INVITE' ? (
                '——'
              ) : (
                ''
              )
            ) : (
              <>
                <span>{collab.amount}</span> {collab.currency}
              </>
            )}
          </div>
        </div>
        <div className={styles.headRow}>
          <div className={styles.rowSubtitle}>{collab.campaign_name}</div>
          {type === 'DECLINED_OR_EXPIRED' && (
            <div className={styles.infoStatus}>
              {collab.status === 'INVITE_EXPIRED' && (
                <>
                  <IconExpired />
                  <span className={styles.expired}>{t('collaborations.expired')}</span>
                </>
              )}
              {collab.status === 'INVITE_DECLINED' && (
                <>
                  <IconDeclined />
                  <span>{t('campaignTab.declined')}</span>
                </>
              )}
            </div>
          )}
          {type === 'DONE' && (
            <div className={`${styles.paidStatus} ${styles[collab.status]}`}>
              {t(paymentStatus[collab.status as 'DONE' | 'DONE_AND_PAID'])}
            </div>
          )}
        </div>
        {type === 'INVITE' && (
          <div className={styles.headRow}>
            <div className={styles.infoDeadline}>
              {t('collaborations.expiredIn')}:{getExpirationMessage(collab.date_expire)}
              <Tooltip
                title={
                  <Trans
                    i18nKey="collaborations.expireMsg"
                    values={{
                      days: dayjs(collab.date_expire).diff(dayjs(), 'day')
                        ? dayjs(collab.date_expire).diff(dayjs(), 'day') + 1
                        : dayjs(collab.date_expire).diff(dayjs(), 'day')
                    }}
                    components={{
                      1: <span />
                    }}
                  />
                }
              >
                <Info size={14} color="#474E5450" />
              </Tooltip>
            </div>
            <div className={styles.infoBriefButton}>
              <Button theme="cta">{t('inviteView.firstCollabModal.button')}</Button>
            </div>
          </div>
        )}
      </div>
      <div onClick={(e) => e.stopPropagation()}>
        {displayedBeats.map((b) => (
          <ItemComponent
            key={b.beat_id}
            className={`${styles.collabItem} ${['ONGOING', 'DONE'].includes(type) ? styles.collabItemHover : ''}`}
            href={
              ['ONGOING', 'DONE'].includes(type)
                ? `/connect/collaborations/${collab.collaboration_id}?beat=${b.beat_id}`
                : undefined
            }
            onClick={
              onBeatClick &&
              ((e) => {
                e.stopPropagation();
                onBeatClick(b.beat_id);
              })
            }
          >
            <div
              className={`${styles.headName} ${!collab?.brand_logo_url ? styles.noSpacing : ''}`}
            >
              {b.beat_name}
            </div>
            <div className={styles.headPlatform}>
              {PlatformIcon(b.platform)}
              <div className={styles.platforms}>
                {b.products &&
                  Object.entries(b.products)
                    .map(([key, value]) => ({ key, value }))
                    .map(({ key, value }) => (
                      <div key={key} className={styles.platformItem}>
                        <span>{value}</span> {key.replace(/_/g, ' ')}
                      </div>
                    ))}
              </div>
            </div>
            <div
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              className={`${styles.headDraftDate} ${styles.hide} ${b.date_draft && getDateClass('Draft', b.status, b.date_draft) && styles[getDateClass('Draft', b.status, b.date_draft)!]}`}
            >
              {b.date_draft
                ? `${dayjs(b.date_draft).format('DD.MM.YY')} ${getDateClass('Draft', b.status, b.date_draft) === 'missed' ? '🔥' : ''}`
                : '——'}
            </div>
            <div className={styles.headPublicationDate}>
              {type !== 'INVITE' ? (
                b.date_publish ? (
                  <span
                    className={(() => {
                      const dateClass = getDateClass('Publication', b.status, b.date_publish);
                      return dateClass ? styles[dateClass] : undefined;
                    })()}
                  >
                    {`${dayjs(b.date_publish).format('DD.MM.YY')} ${getDateClass('Publication', b.status, b.date_publish) === 'missed' ? '🔥' : ''}`}
                  </span>
                ) : (
                  '——'
                )
              ) : b?.dates_publish ? (
                b.dates_publish.length > 1 ? (
                  `${b.dates_publish.length} ${t('collaborations.options')}`
                ) : (
                  <span
                    className={(() => {
                      const dateClass = getDateClass('Publication', b.status, b.dates_publish[0]);
                      return dateClass ? styles[dateClass] : undefined;
                    })()}
                  >
                    {`${dayjs(b.dates_publish[0]).format('DD.MM.YY')} ${getDateClass('Publication', b.status, b.dates_publish[0]) === 'missed' ? '🔥' : ''}`}
                  </span>
                )
              ) : (
                '——'
              )}
            </div>
            {type !== 'DECLINED_OR_EXPIRED' && (
              <div className={styles.headReportDate}>
                {type !== 'INVITE' &&
                  (b?.date_result ? (
                    <span
                      className={(() => {
                        const dateClass = getDateClass('Report', b.status, b.date_result);
                        return dateClass ? styles[dateClass] : undefined;
                      })()}
                    >
                      {`${dayjs(b.date_result).format('DD.MM.YY')} ${getDateClass('Report', b.status, b.date_result) === 'missed' ? '🔥' : ''}`}
                    </span>
                  ) : (
                    '——'
                  ))}
              </div>
            )}
            {type !== 'DECLINED_OR_EXPIRED' && (
              <div className={styles.headStatus}>
                {type !== 'INVITE' && (
                  <Tag label={beatStatuses[b.status].label} color={beatStatuses[b.status].color} />
                )}
              </div>
            )}
            <div className={styles.beatSm}>
              <div className={styles.beatRowSm}>
                <span className={styles.grow}>{b.beat_name}</span>
                <span className={styles.shrink}>
                  {(type === 'DONE' || type === 'ONGOING') && (
                    <Tag
                      label={beatStatuses[b.status].label}
                      color={beatStatuses[b.status].color}
                    />
                  )}
                </span>
              </div>
              <div className={styles.beatRowSm}>
                <span className={styles.headPlatform}>
                  {PlatformIcon(b.platform)}
                  <div className={styles.platforms}>
                    {b.products &&
                      Object.entries(b.products)
                        .map(([key, value]) => ({ key, value }))
                        .map(({ key, value }) => (
                          <div key={key} className={styles.platformItem}>
                            <span>{value}</span> {key.replace(/_/g, ' ')}
                          </div>
                        ))}
                  </div>
                </span>
                <span className={styles.shrink}>
                  {type === 'ONGOING' && (
                    <span
                      className={(() => {
                        const res = getRelevantDate(b);
                        if (res) {
                          const { statusType, status, date } = res;
                          const dateClass = getDateClass(
                            statusType as 'Publication' | 'Report' | 'Draft',
                            status,
                            date
                          );
                          return dateClass ? styles[dateClass] : undefined;
                        }
                        return '';
                      })()}
                    >
                      {(() => {
                        const relevantDate = getRelevantDate(b);
                        if (!relevantDate) return '—';
                        const { statusType, status, date } = relevantDate;

                        const formattedDate = dayjs(relevantDate.date).format('DD.MM.YY');
                        const statusIcon =
                          getDateClass(
                            statusType as 'Publication' | 'Report' | 'Draft',
                            status,
                            date
                          ) === 'missed'
                            ? '🔥'
                            : '';

                        return `${formattedDate} ${statusIcon}`;
                      })()}
                    </span>
                  )}
                  {['INVITE', 'DONE', 'DECLINED_OR_EXPIRED'].includes(type) && (
                    <span>{b.date_draft ? dayjs(b.date_draft).format('DD.MM.YY') : '-'}</span>
                  )}
                </span>
              </div>
            </div>
          </ItemComponent>
        ))}
        {(collab.beats.length > BEATS_TO_SHOW ||
          (type === 'DECLINED_OR_EXPIRED' && collab.invite_feedback) ||
          (type === 'INVITE' && collab.invite_notes)) && (
          <div
            onClick={(e) => e.stopPropagation()}
            className={`${styles.collabItem} ${styles.expand}`}
          >
            <div className={styles.expandInfo}>
              {type === 'DECLINED_OR_EXPIRED' && !!collab.invite_feedback && (
                <>
                  <div className={styles.expandInfoImg}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_6467_32259)">
                        <path
                          d="M15.9998 7.49789C15.9034 5.9379 15.3525 4.44023 14.4152 3.1895C13.4779 1.93878 12.1951 0.989683 10.7249 0.459218C9.25473 -0.0712477 7.66144 -0.15989 6.14148 0.204218C4.62152 0.568326 3.24134 1.36927 2.17108 2.5083C1.10082 3.64733 0.38727 5.07466 0.118404 6.61433C-0.150462 8.15399 0.0371069 9.73868 0.657987 11.173C1.27887 12.6074 2.30591 13.8287 3.61251 14.6864C4.91911 15.5441 6.44815 16.0007 8.01111 15.9999H12.6664C13.5502 15.999 14.3976 15.6475 15.0225 15.0226C15.6474 14.3977 15.9989 13.5503 15.9998 12.6666V7.49789ZM14.6664 12.6666C14.6664 13.197 14.4557 13.7057 14.0807 14.0808C13.7056 14.4558 13.1969 14.6666 12.6664 14.6666H8.01111C7.0704 14.6661 6.14034 14.4674 5.28157 14.0834C4.42281 13.6994 3.65463 13.1387 3.02711 12.4379C2.39656 11.7374 1.92306 10.9102 1.63837 10.0117C1.35368 9.11329 1.26437 8.16435 1.37644 7.22855C1.5534 5.75252 2.21657 4.37735 3.2614 3.31984C4.30624 2.26233 5.67331 1.58263 7.14711 1.38789C7.4346 1.35185 7.72404 1.3336 8.01378 1.33322C9.56743 1.32898 11.073 1.87182 12.2664 2.86655C12.9635 3.44585 13.5356 4.16066 13.9481 4.96764C14.3607 5.77463 14.6051 6.65698 14.6664 7.56122V12.6666Z"
                          fill="#EE7541"
                        />
                        <path
                          d="M5.33317 6.00008H7.99984C8.17665 6.00008 8.34622 5.92984 8.47124 5.80482C8.59627 5.6798 8.6665 5.51023 8.6665 5.33341C8.6665 5.1566 8.59627 4.98703 8.47124 4.86201C8.34622 4.73699 8.17665 4.66675 7.99984 4.66675H5.33317C5.15636 4.66675 4.98679 4.73699 4.86177 4.86201C4.73674 4.98703 4.6665 5.1566 4.6665 5.33341C4.6665 5.51023 4.73674 5.6798 4.86177 5.80482C4.98679 5.92984 5.15636 6.00008 5.33317 6.00008Z"
                          fill="#EE7541"
                        />
                        <path
                          d="M10.6665 7.33325H5.33317C5.15636 7.33325 4.98679 7.40349 4.86177 7.52851C4.73674 7.65354 4.6665 7.82311 4.6665 7.99992C4.6665 8.17673 4.73674 8.3463 4.86177 8.47132C4.98679 8.59635 5.15636 8.66659 5.33317 8.66659H10.6665C10.8433 8.66659 11.0129 8.59635 11.1379 8.47132C11.2629 8.3463 11.3332 8.17673 11.3332 7.99992C11.3332 7.82311 11.2629 7.65354 11.1379 7.52851C11.0129 7.40349 10.8433 7.33325 10.6665 7.33325Z"
                          fill="#EE7541"
                        />
                        <path
                          d="M10.6665 10H5.33317C5.15636 10 4.98679 10.0702 4.86177 10.1953C4.73674 10.3203 4.6665 10.4899 4.6665 10.6667C4.6665 10.8435 4.73674 11.013 4.86177 11.1381C4.98679 11.2631 5.15636 11.3333 5.33317 11.3333H10.6665C10.8433 11.3333 11.0129 11.2631 11.1379 11.1381C11.2629 11.013 11.3332 10.8435 11.3332 10.6667C11.3332 10.4899 11.2629 10.3203 11.1379 10.1953C11.0129 10.0702 10.8433 10 10.6665 10Z"
                          fill="#EE7541"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_6467_32259">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <div className={styles.expandInfoMsg}>{collab.invite_feedback}</div>
                </>
              )}
              {type === 'INVITE' && !!collab.invite_notes && (
                <>
                  <div className={styles.expandInfoImg}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_6467_32259)">
                        <path
                          d="M15.9998 7.49789C15.9034 5.9379 15.3525 4.44023 14.4152 3.1895C13.4779 1.93878 12.1951 0.989683 10.7249 0.459218C9.25473 -0.0712477 7.66144 -0.15989 6.14148 0.204218C4.62152 0.568326 3.24134 1.36927 2.17108 2.5083C1.10082 3.64733 0.38727 5.07466 0.118404 6.61433C-0.150462 8.15399 0.0371069 9.73868 0.657987 11.173C1.27887 12.6074 2.30591 13.8287 3.61251 14.6864C4.91911 15.5441 6.44815 16.0007 8.01111 15.9999H12.6664C13.5502 15.999 14.3976 15.6475 15.0225 15.0226C15.6474 14.3977 15.9989 13.5503 15.9998 12.6666V7.49789ZM14.6664 12.6666C14.6664 13.197 14.4557 13.7057 14.0807 14.0808C13.7056 14.4558 13.1969 14.6666 12.6664 14.6666H8.01111C7.0704 14.6661 6.14034 14.4674 5.28157 14.0834C4.42281 13.6994 3.65463 13.1387 3.02711 12.4379C2.39656 11.7374 1.92306 10.9102 1.63837 10.0117C1.35368 9.11329 1.26437 8.16435 1.37644 7.22855C1.5534 5.75252 2.21657 4.37735 3.2614 3.31984C4.30624 2.26233 5.67331 1.58263 7.14711 1.38789C7.4346 1.35185 7.72404 1.3336 8.01378 1.33322C9.56743 1.32898 11.073 1.87182 12.2664 2.86655C12.9635 3.44585 13.5356 4.16066 13.9481 4.96764C14.3607 5.77463 14.6051 6.65698 14.6664 7.56122V12.6666Z"
                          fill="#000000"
                        />
                        <path
                          d="M5.33317 6.00008H7.99984C8.17665 6.00008 8.34622 5.92984 8.47124 5.80482C8.59627 5.6798 8.6665 5.51023 8.6665 5.33341C8.6665 5.1566 8.59627 4.98703 8.47124 4.86201C8.34622 4.73699 8.17665 4.66675 7.99984 4.66675H5.33317C5.15636 4.66675 4.98679 4.73699 4.86177 4.86201C4.73674 4.98703 4.6665 5.1566 4.6665 5.33341C4.6665 5.51023 4.73674 5.6798 4.86177 5.80482C4.98679 5.92984 5.15636 6.00008 5.33317 6.00008Z"
                          fill="#000000"
                        />
                        <path
                          d="M10.6665 7.33325H5.33317C5.15636 7.33325 4.98679 7.40349 4.86177 7.52851C4.73674 7.65354 4.6665 7.82311 4.6665 7.99992C4.6665 8.17673 4.73674 8.3463 4.86177 8.47132C4.98679 8.59635 5.15636 8.66659 5.33317 8.66659H10.6665C10.8433 8.66659 11.0129 8.59635 11.1379 8.47132C11.2629 8.3463 11.3332 8.17673 11.3332 7.99992C11.3332 7.82311 11.2629 7.65354 11.1379 7.52851C11.0129 7.40349 10.8433 7.33325 10.6665 7.33325Z"
                          fill="#000000"
                        />
                        <path
                          d="M10.6665 10H5.33317C5.15636 10 4.98679 10.0702 4.86177 10.1953C4.73674 10.3203 4.6665 10.4899 4.6665 10.6667C4.6665 10.8435 4.73674 11.013 4.86177 11.1381C4.98679 11.2631 5.15636 11.3333 5.33317 11.3333H10.6665C10.8433 11.3333 11.0129 11.2631 11.1379 11.1381C11.2629 11.013 11.3332 10.8435 11.3332 10.6667C11.3332 10.4899 11.2629 10.3203 11.1379 10.1953C11.0129 10.0702 10.8433 10 10.6665 10Z"
                          fill="#000000"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_6467_32259">
                          <rect width="16" height="16" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </div>
                  <div
                    className={styles.noteBody}
                    dangerouslySetInnerHTML={{
                      __html: collab.invite_notes
                    }}
                  />
                </>
              )}
            </div>
            {collab.beats.length > BEATS_TO_SHOW && (
              <button type="button" onClick={handleShowAll}>
                {showAll ? (
                  <>
                    <span>{t('collaborations.hide')}</span>
                    <ChevronUp size={20} />
                  </>
                ) : (
                  <>
                    <span>{`${t('collaborations.viewAll')} (${collab.beats.length})`}</span>
                    <ChevronDown size={20} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </ItemComponent>
  );
}
